import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardDescription, CardTitle, CompassLogo } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { LoginBranding } from '@/components/login-branding';
import {
  readDecouverteProfile,
  signupSubtitleFor,
  signupTitleFor,
} from '@/features/decouverte/decouverte-profile';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

/**
 * Mobile (< lg) : une seule colonne — logo compact centré, puis la carte.
 * Desktop (≥ lg) : l'écran est coupé en DEUX moitiés plein écran — marque sur
 * fond BLANC à gauche (contraste, texte foncé), connexion sur fond océan à droite.
 *
 * `signup` (`/login?inscription=1`, depuis l'accueil `/decouverte`) : même
 * formulaire (un email inconnu crée le compte), mais l'en-tête parle
 * d'inscription, au profil choisi (« Crée ton compte élève »), et rassure
 * (gratuit, sans carte, sans mot de passe). L'onboarding présélectionne ensuite
 * le rôle.
 *
 * Le logo existe en deux exemplaires (compact `lg:hidden`, grand dans
 * `LoginBranding`) car `CompassLogo` n'a pas de taille responsive : c'est le
 * seul moyen de grossir le logo sur desktop sans toucher au rendu mobile.
 */
export function LoginPage({ signup = false }: { signup?: boolean }) {
  const [profile] = useState(readDecouverteProfile);

  return (
    <div className="min-h-dvh bg-transparent lg:grid lg:h-dvh lg:grid-cols-2">
      {/* Colonne gauche (desktop uniquement) : demi-écran BLANC + marque.
          Fond blanc LITTÉRAL (inline) : en thème océan `bg-white` = surface
          translucide, il laisserait passer le bleu du body. */}
      <div
        className="hidden lg:flex lg:h-dvh lg:items-center lg:justify-center lg:overflow-y-auto lg:px-12"
        style={{ background: 'linear-gradient(165deg, #ffffff 0%, #eff4fb 100%)' }}
      >
        <LoginBranding />
      </div>

      {/* Colonne droite : connexion. Mobile = colonne unique plein écran ;
          desktop = demi-écran océan avec la carte centrée. */}
      <div className="mx-auto flex w-full max-w-md flex-col lg:mx-0 lg:h-dvh lg:max-w-none lg:items-center lg:justify-center lg:overflow-y-auto">
        <header className="native:pt-[calc(env(safe-area-inset-top)+1.5rem)] flex justify-center px-6 pb-2 pt-6 lg:hidden">
          <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
        </header>

        <main className="w-full px-6 pb-16 pt-0 sm:pt-6 lg:max-w-md lg:px-0 lg:py-16">
          <Card className="border-ink-100/60 shadow-lg">
            <OtpAuthForm
              key={signup ? 'signup' : 'login'}
              initialMode={signup ? 'signup' : 'login'}
              renderHeader={
                signup
                  ? ({ step, email }) =>
                      step === 'otp' ? (
                        <>
                          <CardTitle>Vérifie ta boîte mail</CardTitle>
                          <CardDescription>
                            Entre le code à 6 chiffres envoyé à{' '}
                            <strong className="text-ink-700">{email}</strong>.
                          </CardDescription>
                        </>
                      ) : (
                        <>
                          <CardTitle>{signupTitleFor(profile)}</CardTitle>
                          <CardDescription>{signupSubtitleFor(profile)}</CardDescription>
                        </>
                      )
                  : undefined
              }
            />
          </Card>

          {/* Liens discrets sous la carte. `Link` pour les routes internes, `<a>`
              pour le site marketing (hors app). */}
          <nav
            aria-label="Liens secondaires"
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm"
          >
            {signup ? (
              <Link
                to="/login"
                className="text-ink-500 hover:text-ink-700 font-medium transition-colors"
              >
                J’ai déjà un compte
              </Link>
            ) : (
              <Link
                to="/demo"
                className="text-ink-500 hover:text-ink-700 font-medium transition-colors"
              >
                Essayer sans compte
              </Link>
            )}
            <Link
              to="/decouverte"
              className="text-ink-500 hover:text-ink-700 font-medium transition-colors"
            >
              Revoir la présentation
            </Link>
            <a
              href={`${MARKETING_URL}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-500 hover:text-ink-700 font-medium transition-colors"
            >
              Visiter le site
            </a>
          </nav>
        </main>
      </div>
    </div>
  );
}
